import { DateTime } from 'luxon';
import { DateUtility } from './date.utility';

export enum FiltersRelativeDateOperator {

    None = 'None',

    // Time range presets - Past (minutes/hours)
    Last5Minutes = 'Last 5 Minutes',
    Last15Minutes = 'Last 15 Minutes',
    Last30Minutes = 'Last 30 Minutes',
    Last1Hour = 'Last 1 Hour',
    Last2Hours = 'Last 2 Hours',
    Last4Hours = 'Last 4 Hours',
    Last6Hours = 'Last 6 Hours',
    Last8Hours = 'Last 8 Hours',
    Last12Hours = 'Last 12 Hours',
    Last24Hours = 'Last 24 Hours',
    Last48Hours = 'Last 48 Hours',
    Last72Hours = 'Last 72 Hours',
    Last7Days = 'Last 7 Days',

    // Time range presets - Future (minutes/hours)
    Next5Minutes = 'Next 5 Minutes',
    Next15Minutes = 'Next 15 Minutes',
    Next30Minutes = 'Next 30 Minutes',
    Next1Hour = 'Next 1 Hour',
    Next2Hours = 'Next 2 Hours',
    Next4Hours = 'Next 4 Hours',
    Next6Hours = 'Next 6 Hours',
    Next8Hours = 'Next 8 Hours',
    Next12Hours = 'Next 12 Hours',
    Next24Hours = 'Next 24 Hours',
    Next48Hours = 'Next 48 Hours',
    Next72Hours = 'Next 72 Hours',

    // Dynamic time operators
    LastXMinutes = 'Last X Minutes',
    LastXHours = 'Last X Hours',
    NextXMinutes = 'Next X Minutes',
    NextXHours = 'Next X Hours',

    Today = 'Today',
    Yesterday = 'Yesterday',
    Tomorrow = 'Tomorrow',

    On = 'On',
    OnOrBefore = 'On Or Before',
    OnOrAfter = 'On Or After',
    Between = 'Between',

    OnTime = 'On Time',
    OnOrBeforeTime = 'On Or Before Time',
    OnOrAfterTime = 'On Or After Time',
    BetweenTime = 'Between Time',

    OnOrBeforeToday = 'On Or Before Today',
    OnOrBeforeYesterday = 'On Or Before Yesterday',
    OnOrBeforeTomorrow = 'On Or Before Tomorrow',
    OnOrBeforeXDaysAgo = 'On Or Before X Days Ago',
    OnOrBeforeXDaysFromNow = 'On Or Before X Days From Now',

    OnOrAfterToday = 'On Or After Today',
    OnOrAfterYesterday = 'On Or After Yesterday',
    OnOrAfterTomorrow = 'On Or After Tomorrow',
    OnOrAfterXDaysAgo = 'On Or After X Days Ago',
    OnOrAfterXDaysFromNow = 'On Or After X Days From Now',

    LastXDays = 'Last X Days',
    NextXDays = 'Next X Days',

    ThisWeek = 'This Week',
    LastWeek = 'Last Week',
    NextWeek = 'Next Week',
    WeekToDate = 'Week To Date',

    ThisMonth = 'This Month',
    LastMonth = 'Last Month',
    NextMonth = 'Next Month',

    ThisYear = 'This Year',
    LastYear = 'Last Year',
    NextYear = 'Next Year',

    LastXWeeks = 'Last X Weeks',
    LastXMonths = 'Last X Months',
    LastXYears = 'Last X Year',

    NextXWeeks = 'Next X Weeks',
    NextXMonths = 'Next X Months',
    NextXYears = 'Next X Years',

    ThisQuarter = 'This Quarter',
    LastQuarter = 'Last Quarter',
    NextQuarter = 'Next Quarter',
    LastXQuarters = 'Last X Quarters',
    NextXQuarters = 'Next X Quarters',

    MonthToDate = 'Month To Date',
    QuarterToDate = 'Quarter To Date',
    YearToDate = 'Year To Date',

    ThisFiscalYear = 'This Fiscal Year',
    LastFiscalYear = 'Last Fiscal Year',
    NextFiscalYear = 'Next Fiscal Year',

    LastXFiscalYears = 'Last X Fiscal Years',
    NextXFiscalYears = 'Next X Fiscal Years',
    FiscalYearToDate = 'Fiscal Year To Date',

    ThisFiscalQuarter = 'This Fiscal Quarter',
    LastFiscalQuarter = 'Last Fiscal Quarter',
    NextFiscalQuarter = 'Next Fiscal Quarter',

    LastXFiscalQuarters = 'Last X Fiscal Quarters',
    NextXFiscalQuarters = 'Next X Fiscal Quarters',
    FiscalQuarterToDate = 'Fiscal Quarter To Date',

}

export interface FiltersRelativeDateMethod {
    operator: FiltersRelativeDateOperator;
    value?: Date | number;
    value2?: Date;
    includeCurrent?: boolean;
    timezone?: string;
    fiscalYearStartMonth?: number;
    fiscalYearStartDay?: number;
}

export class FiltersRelativeDateUtility {

    public static getFilter(method: FiltersRelativeDateMethod) {

        const { operator, value, value2 } = method;
        let { fiscalYearStartDay, fiscalYearStartMonth } = method;

        const zone = method.timezone
            ? method.timezone
            : 'utc';
        // const timezone = 'utc';

        if (!fiscalYearStartDay || !fiscalYearStartMonth) {
            //If neither of these have been provided, we set it based on Australian fiscal year
            fiscalYearStartMonth = 7;
            fiscalYearStartDay = 1;
        }

        switch (operator) {

            // Time range presets (minutes/hours)
            // Timezone doesn't matter for relative time - "5 minutes ago" is the same instant regardless of timezone
            case FiltersRelativeDateOperator.Last5Minutes: {
                const now = DateTime.utc();
                return { $gte: now.minus({ minutes: 5 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last15Minutes: {
                const now = DateTime.utc();
                return { $gte: now.minus({ minutes: 15 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last30Minutes: {
                const now = DateTime.utc();
                return { $gte: now.minus({ minutes: 30 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last1Hour: {
                const now = DateTime.utc();
                return { $gte: now.minus({ hours: 1 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last4Hours: {
                const now = DateTime.utc();
                return { $gte: now.minus({ hours: 4 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last12Hours: {
                const now = DateTime.utc();
                return { $gte: now.minus({ hours: 12 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last24Hours: {
                const now = DateTime.utc();
                return { $gte: now.minus({ hours: 24 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last2Hours: {
                const now = DateTime.utc();
                return { $gte: now.minus({ hours: 2 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last6Hours: {
                const now = DateTime.utc();
                return { $gte: now.minus({ hours: 6 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last8Hours: {
                const now = DateTime.utc();
                return { $gte: now.minus({ hours: 8 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last48Hours: {
                const now = DateTime.utc();
                return { $gte: now.minus({ hours: 48 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last72Hours: {
                const now = DateTime.utc();
                return { $gte: now.minus({ hours: 72 }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.Last7Days: {
                const now = DateTime.utc();
                return { $gte: now.minus({ days: 7 }).toJSDate(), $lte: now.toJSDate() };
            }

            // Future time presets
            case FiltersRelativeDateOperator.Next5Minutes: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ minutes: 5 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next15Minutes: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ minutes: 15 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next30Minutes: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ minutes: 30 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next1Hour: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours: 1 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next2Hours: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours: 2 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next4Hours: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours: 4 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next6Hours: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours: 6 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next8Hours: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours: 8 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next12Hours: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours: 12 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next24Hours: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours: 24 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next48Hours: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours: 48 }).toJSDate() };
            }
            case FiltersRelativeDateOperator.Next72Hours: {
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours: 72 }).toJSDate() };
            }

            // Dynamic time operators
            case FiltersRelativeDateOperator.LastXMinutes: {
                const minutes = value as number;
                const now = DateTime.utc();
                return { $gte: now.minus({ minutes }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.LastXHours: {
                const hours = value as number;
                const now = DateTime.utc();
                return { $gte: now.minus({ hours }).toJSDate(), $lte: now.toJSDate() };
            }
            case FiltersRelativeDateOperator.NextXMinutes: {
                const minutes = value as number;
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ minutes }).toJSDate() };
            }
            case FiltersRelativeDateOperator.NextXHours: {
                const hours = value as number;
                const now = DateTime.utc();
                return { $gte: now.toJSDate(), $lte: now.plus({ hours }).toJSDate() };
            }

            case FiltersRelativeDateOperator.On: {
                //e.g. user filters 'On' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: on this day 15/10/2024
                //Actual query in DB: $gte 14/10/2024-13:00 (UTC) -> $lt 14/10/2024-13:00 (UTC)

                const filterDateLocal = DateUtility.getDateTime(value as any, zone);

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                const toDateUTC = fromDateUTC.plus({ days: 1 });

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.Between: {
                const filterDateLocal = DateUtility.getDateTime(value as any, zone);
                const toDateLocal = DateUtility.getDateTime(value2 as any, zone);
                //e.g. user filters 'Between' at local time: 15/10/2024-08:00AM (+11 AU)
                // from: start of first day, to: end of last day (inclusive)

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                const toDateUTC = toDateLocal.endOf('day').toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lte: toDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrBefore: {
                //e.g. user filters 'On Or Before' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: On 15/10/2024 Or less than it
                //Actual DB query: $lt 15/10/2024-13:00 (UTC)

                const filterDateLocal = DateUtility.getDateTime(value as any, zone);

                const toDateUTC = filterDateLocal.endOf('day').toUTC();

                return { $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.OnOrAfter: {
                //e.g. user filters 'OnOrAfter' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: On 15/10/2024 Or greater than it
                //Actual DB query: $gte 14/10/2024-13:00 (UTC)

                const filterDateLocal = DateUtility.getDateTime(value as any, zone);

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();

                return { $gte: fromDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.Today: {
                //e.g. user filters 'Today' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: $gte 15/10/2024-00:00 (+11 AU) -> $lt 16/10/2024-00:00 (+11 AU)
                //Actual query in DB: $gte 14/10/2024-13:00 (UTC) -> $lt 14/10/2024-13:00 (UTC)

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                const toDateUTC = fromDateUTC.plus({ days: 1 });

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.Yesterday: {
                //e.g. user filters 'Yesterday' at local time: 15/10/2024-08:00AM (+11 AU)
                //User expected: $gte 14/10/2024-00:00 (+11 AU) -> $lt 15/10/2024-00:00 (+11 AU)
                //Actual query in DB: $gte 13/10/2024-13:00 (UTC) -> $lt 14/10/2024-13:00 (UTC)

                const filterDateLocal = DateTime.local({ zone }).minus({ days: 1 });

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                const toDateUTC = fromDateUTC.plus({ days: 1 });

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.Tomorrow: {
                const filterDateLocal = DateTime.local({ zone }).plus({ days: 1 });

                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                const toDateUTC = fromDateUTC.plus({ days: 1 });

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.LastXDays: {
                //Dont get trapped, the Last X FiltersRelativeDates should be the opposite of the Next X FiltersRelativeDates
                //not as simple as swiching the plus and minus :)

                // 1st e.g. filter 'Last 3 Days' at local time: 15/10/2024-08:00AM (+11 AU)
                // 1st e.g. Actual query in DB: $gte 11/10/2024-13:00(UTC) -> $lt 14/10/2024-12:59:59(UTC)

                // 2nd e.g. filter 'Last 3 Days include today' at local time: 15/10/2024-08:00AM (+11 AU)
                // 2nd e.g. Actual query in DB: $gte 12/10/2024-13:00(UTC) -> $lt 15/10/2024-12:59:59(UTC)

                const numberOfDays = value as number;
                const includeToday = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('day')
                    .minus({
                        days: includeToday
                            ? numberOfDays - 1
                            : numberOfDays
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('day')
                    .minus({
                        days: includeToday
                            ? 0
                            : 1
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextXDays: {
                // 1st e.g. filter 'Next 3 Days' at local time: 15/10/2024-08:00AM (+11 AU)
                // 1st e.g. Actual query in DB: $gte 15/10/2024-13:00(UTC) -> $lt 17/10/2024-12:59:59(UTC)

                // 2nd e.g. filter 'Next 3 Days include today' at local time: 15/10/2024-08:00AM (+11 AU)
                // 2nd e.g. Actual query in DB: $gte 14/10/2024-13:00(UTC) -> $lt 16/10/2024-12:59:59(UTC)

                const numberOfDays = value as number;
                const includeToday = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('day')
                    .plus({
                        days: includeToday
                            ? 0
                            : 1
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('day')
                    .plus({
                        days: includeToday
                            ? numberOfDays - 1
                            : numberOfDays
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.ThisWeek: {
                //e.g. user filters 'ThisWeek' at local time: 15/10/2024-08:00AM (+11 AU)
                // User expected: 14/10/2024-00:00 (+11 AU) -> 19/10/2024-23:59:59:999 (+11 AU)
                //Actual query in DB: $gte 13/10/2024-13:00 (UTC) -> $lt 19/10/2024-12:59:59 (UTC)

                const filterDateLocal = DateTime.local({ zone });

                const startOfWeekUTC = filterDateLocal.startOf('week').toUTC();
                const endOfWeekUTC = filterDateLocal.endOf('week').toUTC();

                return { $gte: startOfWeekUTC.toJSDate(), $lt: endOfWeekUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastWeek: {
                // Previous calendar week (Mon-Sun)
                const filterDateLocal = DateTime.local({ zone });

                const startOfLastWeekUTC = filterDateLocal.minus({ weeks: 1 }).startOf('week').toUTC();
                const endOfLastWeekUTC = filterDateLocal.minus({ weeks: 1 }).endOf('week').toUTC();

                return { $gte: startOfLastWeekUTC.toJSDate(), $lt: endOfLastWeekUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextWeek: {
                // Next calendar week (Mon-Sun)
                const filterDateLocal = DateTime.local({ zone });

                const startOfNextWeekUTC = filterDateLocal.plus({ weeks: 1 }).startOf('week').toUTC();
                const endOfNextWeekUTC = filterDateLocal.plus({ weeks: 1 }).endOf('week').toUTC();

                return { $gte: startOfNextWeekUTC.toJSDate(), $lt: endOfNextWeekUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.WeekToDate: {
                // Start of this week to now
                const filterDateLocal = DateTime.local({ zone });

                const startOfWeekUTC = filterDateLocal.startOf('week').toUTC();
                const nowUTC = filterDateLocal.endOf('day').toUTC();

                return { $gte: startOfWeekUTC.toJSDate(), $lt: nowUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextXWeeks: {
                // 1st e.g. filter 'Next 2 Weeks include this week' at local time: 2024 Oct 4 - 08:00 (+11 AU)
                // 1st e.g. Actual query in DB: $gte 2024 Sep 29 - 13:00:00 (UTC) -> $lt 2024 Oct 13 - 12:59:59:999 (UTC)

                // 2nd e.g. filter 'Next 2 Weeks' at local time: 2024 Oct 4 - 08:00 (+11 AU)
                // 2nd e.g. Actual query in DB: $gte 2024 Oct 06 - 13:00:00 (UTC) -> $lt 2024 Oct 20 - 12:59:59:999 (UTC)

                const numberOfWeeks = value as number;
                const includeThisWeek = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('week')
                    .plus({
                        weeks: includeThisWeek
                            ? 0
                            : 1
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('week')
                    .plus({
                        weeks: includeThisWeek
                            ? numberOfWeeks - 1
                            : numberOfWeeks
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastXWeeks: {
                // 1st e.g. filter 'Last 2 Weeks include this week' at local time: 2024 Oct 4 - 08:00 (+11 AU)
                // 1st e.g. expected query on UI: $gte 2024 Sep 23 - 00:00:00 (+11 AU) -> $lt 2024 Oct 06 - 23:59:59:999 (+11 AU)
                // 1st e.g. Actual query in DB: $gte 2024 Sep 22 - 13:00:00 (UTC) -> $lt 2024 Oct 06 - 12:59:59:999 (UTC)

                // 2nd e.g. filter 'Last 2 Weeks' at local time: 2024 Oct 4 - 08:00 (+11 AU)
                // 2nd e.g. expected query on UI: $gte 2024 Sep 16 - 00:00:00 (+11 AU) -> $lt 2024 Sep 29 - 23:59:59:999 (+11 AU)
                // 2nd e.g. Actual query in DB: $gte 2024 Sep 15 - 13:00:00 (UTC) -> $lt 2024 Sep 29 - 12:59:59:999 (UTC)

                const numberOfWeeks = value as number;
                const includeThisWeek = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('week')
                    .minus({
                        weeks: includeThisWeek
                            ? numberOfWeeks - 1
                            : numberOfWeeks
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('week')
                    .minus({
                        weeks: includeThisWeek
                            ? 0
                            : 1
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.ThisMonth: {
                //e.g. user filters 'ThisMonth' at local time: 04/10/2024-08:00AM (+11 AU)
                // User expected: 01/10/2024-00:00 (+11 AU) -> 31/10/2024-23:59:59:999 (+11 AU)
                //Actual query in DB: $gte 30/09/2024-13:00 (UTC) -> $lt 31/10/2024-12:59:59 (UTC)

                const filterDateLocal = DateTime.local({ zone });

                const startOfMonthUTC = filterDateLocal.startOf('month').toUTC();
                const endOfMonthUTC = filterDateLocal.endOf('month').toUTC();

                return { $gte: startOfMonthUTC.toJSDate(), $lt: endOfMonthUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastMonth: {
                // Previous calendar month
                const filterDateLocal = DateTime.local({ zone });

                const startOfLastMonthUTC = filterDateLocal.minus({ months: 1 }).startOf('month').toUTC();
                const endOfLastMonthUTC = filterDateLocal.minus({ months: 1 }).endOf('month').toUTC();

                return { $gte: startOfLastMonthUTC.toJSDate(), $lt: endOfLastMonthUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextMonth: {
                // Next calendar month
                const filterDateLocal = DateTime.local({ zone });

                const startOfNextMonthUTC = filterDateLocal.plus({ months: 1 }).startOf('month').toUTC();
                const endOfNextMonthUTC = filterDateLocal.plus({ months: 1 }).endOf('month').toUTC();

                return { $gte: startOfNextMonthUTC.toJSDate(), $lt: endOfNextMonthUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextXMonths: {
                const numberOfMonths = value as number;
                const includeThisMonth = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('month')
                    .plus({
                        months: includeThisMonth
                            ? 0
                            : 1
                    })
                    .toUTC();

                // Shift before endOf: shifting from the end of a short month clamps the day (30 Sep + 1 month = 30 Oct) and drops the 31st
                const toDateUTC = filterDateLocal
                    .startOf('month')
                    .plus({
                        months: includeThisMonth
                            ? numberOfMonths - 1
                            : numberOfMonths
                    })
                    .endOf('month')
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.MonthToDate: {
                const filterDateLocal = DateTime.local({ zone });

                const startOfMonthUTC = filterDateLocal.startOf('month').toUTC();
                const toDateUtc = filterDateLocal.endOf('day').toUTC();

                return { $gte: startOfMonthUTC.toJSDate(), $lt: toDateUtc.toJSDate() };
            }
            case FiltersRelativeDateOperator.LastXMonths: {
                const numberOfMonths = value as number;
                const includeThisMonth = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('month')
                    .minus({
                        months: includeThisMonth
                            ? numberOfMonths - 1
                            : numberOfMonths
                    })
                    .toUTC();

                // Shift before endOf: shifting from the end of a short month clamps the day (30 Sep - 1 month = 30 Aug) and drops the 31st
                const toDateUTC = filterDateLocal
                    .startOf('month')
                    .minus({
                        months: includeThisMonth
                            ? 0
                            : 1
                    })
                    .endOf('month')
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.ThisQuarter: {
                const filterDateLocal = DateTime.local({ zone });

                const startOfQuarterUTC = filterDateLocal.startOf('quarter').toUTC();
                const endOfQuarterUTC = filterDateLocal.endOf('quarter').toUTC();

                return { $gte: startOfQuarterUTC.toJSDate(), $lt: endOfQuarterUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastQuarter: {
                const filterDateLocal = DateTime.local({ zone });

                const startOfQuarterUTC = filterDateLocal.minus({ quarters: 1 }).startOf('quarter').toUTC();
                const endOfQuarterUTC = filterDateLocal.minus({ quarters: 1 }).endOf('quarter').toUTC();

                return { $gte: startOfQuarterUTC.toJSDate(), $lt: endOfQuarterUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.LastXQuarters: {
                const numberOfQuarters = value as number;
                const includeThisQuarter = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .minus({
                        quarters: includeThisQuarter
                            ? numberOfQuarters - 1
                            : numberOfQuarters
                    })
                    .startOf('quarter')
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .minus({
                        quarters: includeThisQuarter
                            ? 0
                            : 1
                    })
                    .endOf('quarter')
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.NextXQuarters: {
                const numberOfQuarters = value as number;
                const includeThisQuarter = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .plus({
                        quarters: includeThisQuarter
                            ? 0
                            : 1
                    })
                    .startOf('quarter')
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .plus({
                        quarters: includeThisQuarter
                            ? numberOfQuarters - 1
                            : numberOfQuarters
                    })
                    .endOf('quarter')
                    .toUTC();
                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.NextQuarter: {
                const filterDateLocal = DateTime.local({ zone });

                const startOfQuarterUTC = filterDateLocal.plus({ quarters: 1 }).startOf('quarter').toUTC();
                const endOfQuarterUTC = filterDateLocal.plus({ quarters: 1 }).endOf('quarter').toUTC();

                return { $gte: startOfQuarterUTC.toJSDate(), $lt: endOfQuarterUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.QuarterToDate: {
                const filterDateLocal = DateTime.local({ zone });
                const startOfQuarterUTC = filterDateLocal.startOf('quarter').toUTC();
                const toDateUtc = filterDateLocal.endOf('day').toUTC();
                return { $gte: startOfQuarterUTC.toJSDate(), $lt: toDateUtc.toJSDate() };
            }

            case FiltersRelativeDateOperator.ThisYear: {
                const filterDateLocal = DateTime.local({ zone });

                const startOfYearUTC = filterDateLocal.startOf('year').toUTC();
                const endOfYearUTC = filterDateLocal.endOf('year').toUTC();

                return { $gte: startOfYearUTC.toJSDate(), $lt: endOfYearUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastYear: {
                // Previous calendar year
                const filterDateLocal = DateTime.local({ zone });

                const startOfLastYearUTC = filterDateLocal.minus({ years: 1 }).startOf('year').toUTC();
                const endOfLastYearUTC = filterDateLocal.minus({ years: 1 }).endOf('year').toUTC();

                return { $gte: startOfLastYearUTC.toJSDate(), $lt: endOfLastYearUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.NextYear: {
                // Next calendar year
                const filterDateLocal = DateTime.local({ zone });

                const startOfNextYearUTC = filterDateLocal.plus({ years: 1 }).startOf('year').toUTC();
                const endOfNextYearUTC = filterDateLocal.plus({ years: 1 }).endOf('year').toUTC();

                return { $gte: startOfNextYearUTC.toJSDate(), $lt: endOfNextYearUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.YearToDate: {
                const filterDateLocal = DateTime.local({ zone });

                const startOfYearUTC = filterDateLocal.startOf('year').toUTC();
                const toDateUtc = filterDateLocal.endOf('day').toUTC();

                return { $gte: startOfYearUTC.toJSDate(), $lt: toDateUtc.toJSDate() };
            }
            case FiltersRelativeDateOperator.NextXYears: {
                const numberOfYears = value as number;
                const includeThisYear = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('year')
                    .plus({
                        years: includeThisYear
                            ? 0
                            : 1
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('year')
                    .plus({
                        years: includeThisYear
                            ? numberOfYears - 1
                            : numberOfYears
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.LastXYears: {
                const numberOfYears = value as number;
                const includeThisYear = method.includeCurrent;

                const filterDateLocal = DateTime.local({ zone });

                const fromDateUTC = filterDateLocal
                    .startOf('year')
                    .minus({
                        years: includeThisYear
                            ? numberOfYears - 1
                            : numberOfYears
                    })
                    .toUTC();

                const toDateUTC = filterDateLocal
                    .endOf('year')
                    .minus({
                        years: includeThisYear
                            ? 0
                            : 1
                    })
                    .toUTC();

                return { $gte: fromDateUTC.toJSDate(), $lt: toDateUTC.toJSDate() };

            }
            case FiltersRelativeDateOperator.ThisFiscalYear: {
                //Fiscal year comes from general settings. 
                //We have to assume that the fiscalYearEnd has been passed in
                let { startOfFiscalYear, endOfFiscalYear } = FiltersRelativeDateUtility.getCurrentFiscalStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                return { $gte: startOfFiscalYear.toJSDate(), $lt: endOfFiscalYear.toJSDate() };
            }
            case FiltersRelativeDateOperator.LastFiscalYear: {
                let { startOfFiscalYear, endOfFiscalYear } = FiltersRelativeDateUtility.getCurrentFiscalStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                startOfFiscalYear = startOfFiscalYear.minus({ years: 1 });
                endOfFiscalYear = endOfFiscalYear.minus({ years: 1 });
                console.log(`Fiscal Year Start: ${startOfFiscalYear.toString()}`);
                console.log(`Fiscal Year End: ${endOfFiscalYear.toString()}`);
                return { $gte: startOfFiscalYear.toJSDate(), $lt: endOfFiscalYear.toJSDate() };
            }
            case FiltersRelativeDateOperator.NextFiscalYear: {

                let { startOfFiscalYear, endOfFiscalYear } = FiltersRelativeDateUtility.getCurrentFiscalStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                startOfFiscalYear = startOfFiscalYear.plus({ years: 1 });
                endOfFiscalYear = endOfFiscalYear.plus({ years: 1 });
                console.log(`Fiscal Year Start: ${startOfFiscalYear.toString()}`);
                console.log(`Fiscal Year End: ${endOfFiscalYear.toString()}`);
                return { $gte: startOfFiscalYear.toJSDate(), $lt: endOfFiscalYear.toJSDate() };
            }
            case FiltersRelativeDateOperator.LastXFiscalYears: {

                const numberOfYears = value as number;
                const includeThisYear = method.includeCurrent;

                let { startOfFiscalYear, endOfFiscalYear } = FiltersRelativeDateUtility.getCurrentFiscalStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                startOfFiscalYear = startOfFiscalYear.minus({ years: includeThisYear ? numberOfYears - 1 : numberOfYears });
                endOfFiscalYear = endOfFiscalYear.minus({ years: includeThisYear ? 0 : 1 });
                console.log(`Fiscal Year Start: ${startOfFiscalYear.toString()}`);
                console.log(`Fiscal Year End: ${endOfFiscalYear.toString()}`);
                return { $gte: startOfFiscalYear.toJSDate(), $lt: endOfFiscalYear.toJSDate() };
            }
            case FiltersRelativeDateOperator.NextXFiscalYears: {
                //Fiscal year comes from general settings. 
                //We have to assume that the fiscalYearEnd has been passed in
                const numberOfYears = value as number;
                const includeThisYear = method.includeCurrent;

                let { startOfFiscalYear, endOfFiscalYear } = FiltersRelativeDateUtility.getCurrentFiscalStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                startOfFiscalYear = startOfFiscalYear.plus({ years: includeThisYear ? 0 : 1 });
                endOfFiscalYear = endOfFiscalYear.plus({ years: includeThisYear ? numberOfYears - 1 : numberOfYears });
                console.log(`Fiscal Year Start: ${startOfFiscalYear.toString()}`);
                console.log(`Fiscal Year End: ${endOfFiscalYear.toString()}`);
                return { $gte: startOfFiscalYear.toJSDate(), $lt: endOfFiscalYear.toJSDate() };
            }
            case FiltersRelativeDateOperator.FiscalYearToDate: {
                //Fiscal year comes from general settings. 
                //We have to assume that the fiscalYearEnd has been passed in
                const filterDate = DateTime.local({ zone }).toUTC();
                const { startOfFiscalYear } = FiltersRelativeDateUtility.getCurrentFiscalStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                const startOfFiscalYearUTC = startOfFiscalYear.toUTC();
                const toDateUtc = filterDate.endOf('day').toUTC();

                return { $gte: startOfFiscalYearUTC.toJSDate(), $lt: toDateUtc.toJSDate() };
            }
            case FiltersRelativeDateOperator.ThisFiscalQuarter: {
                //Fiscal year comes from general settings. 
                //We have to assume that the fiscalYearEnd has been passed in
                const { startOfFiscalQuarter, endOfFiscalQuarter } = FiltersRelativeDateUtility.getCurrentFiscalQuarterStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);
                console.log(`Fiscal Quarter Start: ${startOfFiscalQuarter.toString()}`);
                console.log(`Fiscal Quarter End: ${endOfFiscalQuarter.toString()}`);
                return { $gte: startOfFiscalQuarter.toUTC().toJSDate(), $lt: endOfFiscalQuarter.toUTC().toJSDate() };
            }
            case FiltersRelativeDateOperator.LastFiscalQuarter: {
                //Fiscal year comes from general settings. 
                //We have to assume that the fiscalYearEnd has been passed in
                let { startOfFiscalQuarter, endOfFiscalQuarter } = FiltersRelativeDateUtility.getCurrentFiscalQuarterStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                startOfFiscalQuarter = startOfFiscalQuarter.minus({ months: 3 });
                endOfFiscalQuarter = startOfFiscalQuarter.plus({ months: 3 }).minus({ days: 1 }).endOf('day');

                console.log(`Fiscal Quarter Start: ${startOfFiscalQuarter.toString()}`);
                console.log(`Fiscal Quarter End: ${endOfFiscalQuarter.toString()}`);
                return { $gte: startOfFiscalQuarter.toUTC().toJSDate(), $lt: endOfFiscalQuarter.toUTC().toJSDate() };
            }
            case FiltersRelativeDateOperator.LastXFiscalQuarters: {
                const numberOfQuarters = value as number;
                const includeThisQuarter = method.includeCurrent;

                let { startOfFiscalQuarter, endOfFiscalQuarter } = FiltersRelativeDateUtility.getCurrentFiscalQuarterStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                const currentStartOfFiscalQuarter = startOfFiscalQuarter;
                startOfFiscalQuarter = startOfFiscalQuarter.minus({ months: (includeThisQuarter ? numberOfQuarters - 1 : numberOfQuarters) * 3 });
                //Jnoon
                endOfFiscalQuarter = currentStartOfFiscalQuarter.minus({ months: (includeThisQuarter ? 0 : 1) * 3 }).plus({ months: 3 }).minus({ days: 1 }).endOf('day');

                console.log(`Fiscal Quarter Start: ${startOfFiscalQuarter.toString()}`);
                console.log(`Fiscal Quarter End: ${endOfFiscalQuarter.toString()}`);
                return { $gte: startOfFiscalQuarter.toUTC().toJSDate(), $lt: endOfFiscalQuarter.toUTC().toJSDate() };
            }
            case FiltersRelativeDateOperator.NextFiscalQuarter: {
                //Fiscal year comes from general settings. 
                //We have to assume that the fiscalYearEnd has been passed in
                let { startOfFiscalQuarter, endOfFiscalQuarter } = FiltersRelativeDateUtility.getCurrentFiscalQuarterStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                startOfFiscalQuarter = startOfFiscalQuarter.plus({ months: 3 });
                endOfFiscalQuarter = startOfFiscalQuarter.plus({ months: 3 }).minus({ days: 1 }).endOf('day');

                console.log(`Fiscal Quarter Start: ${startOfFiscalQuarter.toString()}`);
                console.log(`Fiscal Quarter End: ${endOfFiscalQuarter.toString()}`);
                return { $gte: startOfFiscalQuarter.toUTC().toJSDate(), $lt: endOfFiscalQuarter.toUTC().toJSDate() };
            }
            case FiltersRelativeDateOperator.NextXFiscalQuarters: {
                const numberOfQuarters = value as number;
                const includeThisQuarter = method.includeCurrent;

                let { startOfFiscalQuarter, endOfFiscalQuarter } = FiltersRelativeDateUtility.getCurrentFiscalQuarterStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                startOfFiscalQuarter = startOfFiscalQuarter.plus({ months: (includeThisQuarter ? 0 : 1) * 3 });
                endOfFiscalQuarter = startOfFiscalQuarter.plus({ months: numberOfQuarters * 3 }).minus({ days: 1 }).endOf('day');

                console.log(`Fiscal Quarter Start: ${startOfFiscalQuarter.toString()}`);
                console.log(`Fiscal Quarter End: ${endOfFiscalQuarter.toString()}`);
                return { $gte: startOfFiscalQuarter.toUTC().toJSDate(), $lt: endOfFiscalQuarter.toUTC().toJSDate() };
            }
            case FiltersRelativeDateOperator.FiscalQuarterToDate: {
                //Fiscal year comes from general settings. 
                //We have to assume that the fiscalYearEnd has been passed in
                const filterDate = DateTime.local({ zone }).toUTC();
                const { startOfFiscalQuarter } = FiltersRelativeDateUtility.getCurrentFiscalQuarterStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

                const toDateUtc = filterDate.endOf('day').toUTC();

                return { $gte: startOfFiscalQuarter.toUTC().toJSDate(), $lt: toDateUtc.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrBeforeToday: {
                const toDateUTC = DateTime.local({ zone }).endOf('day').toUTC();
                return { $lte: toDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrBeforeYesterday: {
                const toDateUTC = DateTime.local({ zone }).minus({ days: 1 }).endOf('day').toUTC();
                return { $lte: toDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrBeforeTomorrow: {
                const toDateUTC = DateTime.local({ zone }).plus({ days: 1 }).endOf('day').toUTC();
                return { $lte: toDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrBeforeXDaysAgo: {
                const filterDateLocal = DateTime.local({ zone }).minus({ days: value as number });
                const toDateUTC = filterDateLocal.endOf('day').toUTC();
                return { $lte: toDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrBeforeXDaysFromNow: {
                const filterDateLocal = DateTime.local({ zone }).plus({ days: value as number });
                const toDateUTC = filterDateLocal.endOf('day').toUTC();
                return { $lte: toDateUTC.toJSDate() };
            }

            case FiltersRelativeDateOperator.OnOrAfterToday: {
                const fromDateUTC = DateTime.local({ zone }).startOf('day').toUTC();
                return { $gte: fromDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrAfterYesterday: {
                const fromDateUTC = DateTime.local({ zone }).minus({ days: 1 }).startOf('day').toUTC();
                return { $gte: fromDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrAfterTomorrow: {
                const fromDateUTC = DateTime.local({ zone }).plus({ days: 1 }).startOf('day').toUTC();
                return { $gte: fromDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrAfterXDaysAgo: {
                const filterDateLocal = DateTime.local({ zone }).minus({ days: value as number });
                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                return { $gte: fromDateUTC.toJSDate() };
            }
            case FiltersRelativeDateOperator.OnOrAfterXDaysFromNow: {
                const filterDateLocal = DateTime.local({ zone }).plus({ days: value as number });
                const fromDateUTC = filterDateLocal.startOf('day').toUTC();
                return { $gte: fromDateUTC.toJSDate() };
            }
        }
    }

    public static getCurrentFiscalQuarterStartAndEndDate(zone: string, fiscalYearStartMonth: number, fiscalYearStartDay: number) {
        //Fiscal year comes from general settings.
        //We have to assume that the fiscalYearEnd has been passed in
        const { startOfFiscalYear } = FiltersRelativeDateUtility.getCurrentFiscalStartAndEndDate(zone, fiscalYearStartMonth, fiscalYearStartDay);

        const filterDateLocal = DateTime.local({ zone });

        //Before we start manipulating the date, we should go back to the zone that was passed in
        const startOfFiscalYearLocal = startOfFiscalYear.setZone(zone);
        //We have the start and end of this fiscal year
        //I need to work out the quarters
        const quarterStarts = [
            startOfFiscalYearLocal,
            startOfFiscalYearLocal.plus({ months: 3 }),
            startOfFiscalYearLocal.plus({ months: 6 }),
            startOfFiscalYearLocal.plus({ months: 9 }),
        ];

        //Find the current quarter
        const currentQuarterStartIdx = quarterStarts.findIndex((start) => {
            return filterDateLocal >= start && filterDateLocal < start.plus({ months: 3 });
        });
        const startOfFiscalQuarterLocal = quarterStarts[currentQuarterStartIdx];
        const startOfFiscalQuarter = startOfFiscalQuarterLocal;

        const endOfFiscalQuarter = startOfFiscalQuarterLocal
            .plus({ months: 3 })
            .minus({ days: 1 })
            .endOf('day')

        //Always return local, because we may need to manipulate the date later
        return { startOfFiscalQuarter, endOfFiscalQuarter };
    }

    public static getCurrentFiscalStartAndEndDate(zone: string, fiscalYearStartMonth: number, fiscalYearStartDay: number) {
        //Fiscal year comes from general settings. 
        //We have to assume that the fiscalYearEnd has been passed in
        const filterDate = DateTime.local({ zone }).toUTC();
        let startOfFiscalYear = DateTime.local({ zone }).set({ month: fiscalYearStartMonth, day: fiscalYearStartDay }).startOf('day').toUTC();
        let endOfFiscalYear = DateTime.local({ zone }).set({ month: fiscalYearStartMonth, day: fiscalYearStartDay })
            .plus({ years: 1 })
            .minus({ day: 1 })
            .endOf('day')
            .toUTC();
        if (filterDate < startOfFiscalYear) {
            startOfFiscalYear = startOfFiscalYear.minus({ years: 1 });
            endOfFiscalYear = endOfFiscalYear.minus({ years: 1 });
        }
        return { startOfFiscalYear, endOfFiscalYear };
    }

    public static getLabel(method: FiltersRelativeDateMethod, dateFormat = 'dd/MM/yyyy') {

        if (!method) return null;

        const { operator, value } = method;

        const zone = method.timezone
            ? method.timezone
            : 'utc';

        switch (operator) {

            // Time range presets (minutes/hours)
            case FiltersRelativeDateOperator.Last5Minutes: {
                return 'Last 5 minutes';
            }
            case FiltersRelativeDateOperator.Last15Minutes: {
                return 'Last 15 minutes';
            }
            case FiltersRelativeDateOperator.Last30Minutes: {
                return 'Last 30 minutes';
            }
            case FiltersRelativeDateOperator.Last1Hour: {
                return 'Last hour';
            }
            case FiltersRelativeDateOperator.Last4Hours: {
                return 'Last 4 hours';
            }
            case FiltersRelativeDateOperator.Last12Hours: {
                return 'Last 12 hours';
            }
            case FiltersRelativeDateOperator.Last24Hours: {
                return 'Last 24 hours';
            }
            case FiltersRelativeDateOperator.Last2Hours: {
                return 'Last 2 hours';
            }
            case FiltersRelativeDateOperator.Last6Hours: {
                return 'Last 6 hours';
            }
            case FiltersRelativeDateOperator.Last8Hours: {
                return 'Last 8 hours';
            }
            case FiltersRelativeDateOperator.Last48Hours: {
                return 'Last 48 hours';
            }
            case FiltersRelativeDateOperator.Last72Hours: {
                return 'Last 72 hours';
            }
            case FiltersRelativeDateOperator.Last7Days: {
                return 'Last 7 days';
            }

            // Future time presets
            case FiltersRelativeDateOperator.Next5Minutes: {
                return 'Next 5 minutes';
            }
            case FiltersRelativeDateOperator.Next15Minutes: {
                return 'Next 15 minutes';
            }
            case FiltersRelativeDateOperator.Next30Minutes: {
                return 'Next 30 minutes';
            }
            case FiltersRelativeDateOperator.Next1Hour: {
                return 'Next hour';
            }
            case FiltersRelativeDateOperator.Next2Hours: {
                return 'Next 2 hours';
            }
            case FiltersRelativeDateOperator.Next4Hours: {
                return 'Next 4 hours';
            }
            case FiltersRelativeDateOperator.Next6Hours: {
                return 'Next 6 hours';
            }
            case FiltersRelativeDateOperator.Next8Hours: {
                return 'Next 8 hours';
            }
            case FiltersRelativeDateOperator.Next12Hours: {
                return 'Next 12 hours';
            }
            case FiltersRelativeDateOperator.Next24Hours: {
                return 'Next 24 hours';
            }
            case FiltersRelativeDateOperator.Next48Hours: {
                return 'Next 48 hours';
            }
            case FiltersRelativeDateOperator.Next72Hours: {
                return 'Next 72 hours';
            }

            // Dynamic time operators
            case FiltersRelativeDateOperator.LastXMinutes: {
                return `Last ${value} minutes`;
            }
            case FiltersRelativeDateOperator.LastXHours: {
                return `Last ${value} hours`;
            }
            case FiltersRelativeDateOperator.NextXMinutes: {
                return `Next ${value} minutes`;
            }
            case FiltersRelativeDateOperator.NextXHours: {
                return `Next ${value} hours`;
            }

            case FiltersRelativeDateOperator.On: {
                return `On ${DateUtility.formatDateTime(value as any, zone, dateFormat)}`;
            }
            case FiltersRelativeDateOperator.Between: {
                const fromDate = DateUtility.formatDateTime(value as any, zone, dateFormat);
                const toDate = DateUtility.formatDateTime(method.value2 as any, zone, dateFormat);
                return `${fromDate} - ${toDate}`;
            }
            case FiltersRelativeDateOperator.OnOrBefore: {
                return `On Or Before ${DateUtility.formatDateTime(value as any, zone, dateFormat)}`;
            }
            case FiltersRelativeDateOperator.OnOrAfter: {
                return `On Or After ${DateUtility.formatDateTime(value as any, zone, dateFormat)}`;
            }
            case FiltersRelativeDateOperator.Today: {
                return `Today`;
            }
            case FiltersRelativeDateOperator.Yesterday: {
                return `Yesterday`;
            }
            case FiltersRelativeDateOperator.Tomorrow: {
                return `Tomorrow`;
            }
            case FiltersRelativeDateOperator.LastXDays: {
                return `Last ${value} Days`;
            }
            case FiltersRelativeDateOperator.NextXDays: {
                return `Next ${value} Days`;
            }
            case FiltersRelativeDateOperator.ThisWeek: {
                return `This Week`;
            }
            case FiltersRelativeDateOperator.LastWeek: {
                return `Last Week`;
            }
            case FiltersRelativeDateOperator.NextWeek: {
                return `Next Week`;
            }
            case FiltersRelativeDateOperator.WeekToDate: {
                return `Week To Date`;
            }
            case FiltersRelativeDateOperator.NextXWeeks: {
                return `Next ${value} Weeks`;
            }
            case FiltersRelativeDateOperator.LastXWeeks: {
                return `Last ${value} Weeks`;
            }
            case FiltersRelativeDateOperator.ThisMonth: {
                return `This Month`;
            }
            case FiltersRelativeDateOperator.LastMonth: {
                return `Last Month`;
            }
            case FiltersRelativeDateOperator.NextMonth: {
                return `Next Month`;
            }
            case FiltersRelativeDateOperator.NextXMonths: {
                return `Next ${value} Months`;
            }
            case FiltersRelativeDateOperator.LastXMonths: {
                return `Last ${value} Months`;
            }
            case FiltersRelativeDateOperator.ThisYear: {
                return `This Year`;
            }
            case FiltersRelativeDateOperator.LastYear: {
                return `Last Year`;
            }
            case FiltersRelativeDateOperator.NextYear: {
                return `Next Year`;
            }
            case FiltersRelativeDateOperator.NextXYears: {
                return `Next ${value} Years`;
            }
            case FiltersRelativeDateOperator.LastXYears: {
                return `Last ${value} Years`;
            }
            case FiltersRelativeDateOperator.ThisQuarter: {
                return `This Quarter`;
            }
            case FiltersRelativeDateOperator.NextXQuarters: {
                return `Next ${value} Quarters`;
            }
            case FiltersRelativeDateOperator.LastXQuarters: {
                return `Last ${value} Quarters`;
            }
            case FiltersRelativeDateOperator.LastQuarter: {
                return `Last Quarter`;
            }
            case FiltersRelativeDateOperator.NextQuarter: {
                return `Next Quarter`;
            }
            case FiltersRelativeDateOperator.MonthToDate: {
                return `Month To Date`;
            }
            case FiltersRelativeDateOperator.YearToDate: {
                return `Year To Date`;
            }
            case FiltersRelativeDateOperator.QuarterToDate: {
                return `Quarter To Date`;
            }
            case FiltersRelativeDateOperator.FiscalYearToDate: {
                return `Fiscal Year To Date`;
            }
            case FiltersRelativeDateOperator.ThisFiscalYear: {
                return `This Fiscal Year`;
            }
            case FiltersRelativeDateOperator.LastFiscalYear: {
                return `Last Fiscal Year`;
            }
            case FiltersRelativeDateOperator.NextFiscalYear: {
                return `Next Fiscal Year`;
            }
            case FiltersRelativeDateOperator.LastXFiscalYears: {
                return `Last ${value} Fiscal Years`;
            }
            case FiltersRelativeDateOperator.NextXFiscalYears: {
                return `Next ${value} Fiscal Years`;
            }
            case FiltersRelativeDateOperator.ThisFiscalQuarter: {
                return `This Fiscal Quarter`;
            }
            case FiltersRelativeDateOperator.LastFiscalQuarter: {
                return `Last Fiscal Quarter`;
            }
            case FiltersRelativeDateOperator.NextFiscalQuarter: {
                return `Next Fiscal Quarter`;
            }
            case FiltersRelativeDateOperator.LastXFiscalQuarters: {
                return `Last ${value} Fiscal Quarters`;
            }
            case FiltersRelativeDateOperator.NextXFiscalQuarters: {
                return `Next ${value} Fiscal Quarters`;
            }
            case FiltersRelativeDateOperator.FiscalQuarterToDate: {
                return `Fiscal Quarter To Date`;
            }
            case FiltersRelativeDateOperator.OnOrBeforeToday: {
                return `On Or Before Today`;
            }
            case FiltersRelativeDateOperator.OnOrBeforeYesterday: {
                return `On Or Before Yesterday`;
            }
            case FiltersRelativeDateOperator.OnOrBeforeTomorrow: {
                return `On Or Before Tomorrow`;
            }
            case FiltersRelativeDateOperator.OnOrBeforeXDaysAgo: {
                return `On Or Before ${value} Days Ago`;
            }
            case FiltersRelativeDateOperator.OnOrBeforeXDaysFromNow: {
                return `On Or Before ${value} Days From Now`;
            }
            case FiltersRelativeDateOperator.OnOrAfterToday: {
                return `On Or After Today`;
            }
            case FiltersRelativeDateOperator.OnOrAfterYesterday: {
                return `On Or After Yesterday`;
            }
            case FiltersRelativeDateOperator.OnOrAfterTomorrow: {
                return `On Or After Tomorrow`;
            }
            case FiltersRelativeDateOperator.OnOrAfterXDaysAgo: {
                return `On Or After ${value} Days Ago`;
            }
            case FiltersRelativeDateOperator.OnOrAfterXDaysFromNow: {
                return `On Or After ${value} Days From Now`;
            }

        }
    }
}
